import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import ReactMarkdown from 'react-markdown';
import {
  Brain, Send, Sparkles, Loader2, ArrowRight,
  Target, Mail, Phone, TrendingUp, Zap,
  BarChart3, MessageCircle, CheckCircle2, Rocket, X,
  User, ListTodo, LayoutDashboard, PlayCircle, Mic, MicOff, RotateCcw,
  Paperclip, FileSpreadsheet, Upload
} from 'lucide-react';
import { Confetti } from '../ui/confetti';
import api from '../../lib/api';

const PLACEHOLDER_TEXTS = [
  'Ask the Brain anything...',
  'Show my hot leads',
  'Who should I call today?',
  'Draft a follow-up email',
  'How\'s my pipeline doing?',
  'What should I do right now?',
  'Give me a weekly summary',
  'Find directors in SaaS',
];

const ACTION_ICONS = {
  user: User, mail: Mail, phone: Phone, chart: BarChart3,
  target: Target, tasks: ListTodo, kanban: LayoutDashboard,
  sparkles: Sparkles, zap: Zap, send: Send, check: CheckCircle2,
  brain: Brain, play: PlayCircle,
};

const QUICK_ACTIONS = [
  { label: 'My Pipeline', icon: TrendingUp, query: "How's my pipeline doing?" },
  { label: 'Hot Leads', icon: Target, query: "Show my top hot leads" },
  { label: 'Send Emails', icon: Mail, query: "Any emails ready to send?" },
  { label: 'Next Best Action', icon: Zap, query: "What should I do right now?" },
  { label: 'Call Schedule', icon: Phone, query: "Who should I call today?" },
  { label: 'Weekly Summary', icon: BarChart3, query: "Give me a weekly summary" },
];

function SmartActionButtons({ actions, onAction, loadingAction, pendingConfirm, onConfirm, onCancel }) {
  if (!actions?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="brain-hero-action-buttons">
      {actions.map((a, i) => {
        const Icon = ACTION_ICONS[a.icon] || ArrowRight;
        const isLoading = loadingAction === i;
        const isApiCall = a.action === 'api_call';
        const isConfirming = pendingConfirm === i;

        if (isConfirming) {
          return (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700/50 shadow-sm animate-[slideUp_0.15s_ease-out]" data-testid={`brain-hero-action-confirm-${i}`}>
              <span>{a.confirm_label || `Run ${a.label}?`}</span>
              <button onClick={() => onConfirm(a, i)} className="px-2.5 py-1 rounded-lg bg-emerald-500 text-white text-[11px] font-bold hover:bg-emerald-600 transition-colors" data-testid={`brain-hero-confirm-yes-${i}`}>Yes</button>
              <button onClick={onCancel} className="px-2.5 py-1 rounded-lg bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200 text-[11px] font-bold hover:bg-slate-400 dark:hover:bg-slate-500 transition-colors" data-testid={`brain-hero-confirm-no-${i}`}>Cancel</button>
            </div>
          );
        }

        return (
          <button
            key={i}
            onClick={() => onAction(a, i)}
            disabled={isLoading}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border shadow-sm hover:shadow-md transition-all cursor-pointer disabled:opacity-60 disabled:cursor-wait ${
              isApiCall
                ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 hover:border-emerald-400 dark:hover:border-emerald-600'
                : 'bg-white dark:bg-slate-800 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800/50 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:border-cyan-400 dark:hover:border-cyan-600'
            }`}
            data-testid={`brain-hero-action-btn-${i}`}
          >
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
            {a.label}
            {!isApiCall && <ArrowRight className="h-3 w-3 opacity-40" />}
          </button>
        );
      })}
    </div>
  );
}

function BrainMessage({ text, actions, onAction, loadingAction, pendingConfirm, onConfirm, onCancel }) {
  return (
    <div className="flex gap-3 animate-[slideUp_0.25s_ease-out]">
      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/15 mt-0.5">
        <Brain className="h-4 w-4 text-white" />
      </div>
      <div className="flex-1 min-w-0 max-w-xl">
        <div className="bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl rounded-2xl rounded-tl-md px-5 py-4 shadow-sm border border-white/20 dark:border-white/[0.08] brain-reply">
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200 mb-2 last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>,
              ul: ({ children }) => <ul className="space-y-1.5 my-2">{children}</ul>,
              ol: ({ children }) => <ol className="space-y-1.5 my-2 list-decimal pl-4">{children}</ol>,
              li: ({ children }) => (
                <li className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 shrink-0" />
                  <span className="leading-relaxed">{children}</span>
                </li>
              ),
              code: ({ children }) => (
                <code className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold">{children}</code>
              ),
            }}
          >
            {text}
          </ReactMarkdown>
        </div>
        {actions?.length > 0 && <SmartActionButtons actions={actions} onAction={onAction} loadingAction={loadingAction} pendingConfirm={pendingConfirm} onConfirm={onConfirm} onCancel={onCancel} />}
      </div>
    </div>
  );
}

function UserMessage({ text }) {
  return (
    <div className="flex justify-end animate-[slideUp_0.25s_ease-out]">
      <div className="max-w-md bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-2xl rounded-br-md px-5 py-3 shadow-md shadow-blue-500/15">
        <p className="text-sm leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3 animate-[slideUp_0.25s_ease-out]">
      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0 shadow-lg shadow-cyan-500/15">
        <Brain className="h-4 w-4 text-white animate-pulse" />
      </div>
      <div className="bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl rounded-2xl rounded-tl-md px-5 py-4 shadow-sm border border-white/20 dark:border-white/[0.08]">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <span className="text-xs text-slate-400 ml-1">Thinking...</span>
        </div>
      </div>
    </div>
  );
}

export default function BrainChatHero({ hideWelcome = false, onChatActive }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Voice recognition
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  
  // File attachment for CSV import
  const [attachedFile, setAttachedFile] = useState(null);
  const [parsedLeads, setParsedLeads] = useState(null);
  const fileInputRef = useRef(null);

  // Animated placeholder
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderText, setPlaceholderText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    if (input || messages.length > 0) return;
    const target = PLACEHOLDER_TEXTS[placeholderIndex];
    let charIndex = 0;
    let timeout;

    if (isTyping) {
      const type = () => {
        if (charIndex <= target.length) {
          setPlaceholderText(target.slice(0, charIndex));
          charIndex++;
          timeout = setTimeout(type, 45);
        } else {
          timeout = setTimeout(() => setIsTyping(false), 2000);
        }
      };
      type();
    } else {
      const erase = () => {
        const current = target.slice(0, charIndex);
        if (charIndex >= 0) {
          setPlaceholderText(current);
          charIndex--;
          timeout = setTimeout(erase, 25);
        } else {
          setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_TEXTS.length);
          setIsTyping(true);
        }
      };
      charIndex = target.length;
      erase();
    }

    return () => clearTimeout(timeout);
  }, [placeholderIndex, isTyping, input, messages.length]);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setInput(transcript);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognitionRef.current = recognition;
    return () => { try { recognition.abort(); } catch {} };
  }, []);

  const toggleVoice = useCallback(() => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInput('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  // CSV file attachment handling
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'txt'].includes(ext)) {
      setMessages(prev => [...prev, { role: 'brain', text: 'Please attach a CSV file (.csv) to import leads.' }]);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) {
          setMessages(prev => [...prev, { role: 'brain', text: 'The CSV file appears empty or has no data rows.' }]);
          return;
        }
        const headerLine = lines[0];
        const delimiter = headerLine.includes('\t') ? '\t' : ',';
        const headers = headerLine.split(delimiter).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
        
        // Map common CSV headers to lead fields
        const fieldMap = {
          name: ['name', 'full name', 'fullname', 'contact name', 'lead name', 'contact'],
          email: ['email', 'email address', 'e-mail', 'mail'],
          phone: ['phone', 'phone number', 'telephone', 'mobile', 'cell'],
          company: ['company', 'company name', 'organization', 'org', 'business'],
          source: ['source', 'lead source', 'channel'],
          status: ['status', 'lead status', 'stage'],
          value: ['value', 'deal value', 'amount', 'revenue'],
          notes: ['notes', 'note', 'description', 'comments'],
          address: ['address', 'street'],
          city: ['city', 'town'],
          state: ['state', 'province', 'region'],
          zip_code: ['zip', 'zip code', 'zipcode', 'postal code', 'postal'],
        };
        
        const colIdx = {};
        for (const [field, aliases] of Object.entries(fieldMap)) {
          const idx = headers.findIndex(h => aliases.includes(h));
          if (idx !== -1) colIdx[field] = idx;
        }
        
        if (!colIdx.name && !colIdx.email && !colIdx.company) {
          setMessages(prev => [...prev, { role: 'brain', text: 'Could not find name, email, or company columns in the CSV. Please ensure your CSV has recognizable headers.' }]);
          return;
        }
        
        const leads = [];
        for (let i = 1; i < lines.length; i++) {
          const vals = lines[i].split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
          const lead = {};
          for (const [field, idx] of Object.entries(colIdx)) {
            if (vals[idx]) lead[field] = vals[idx];
          }
          if (lead.name || lead.email || lead.company) {
            lead.name = lead.name || lead.email?.split('@')[0] || 'Unknown';
            if (lead.value) lead.value = parseFloat(lead.value) || 0;
            leads.push(lead);
          }
        }
        
        setAttachedFile({ name: file.name, count: leads.length, headers: Object.keys(colIdx) });
        setParsedLeads(leads);
        setInput(prev => prev || `Import ${leads.length} leads from ${file.name}`);
      } catch {
        setMessages(prev => [...prev, { role: 'brain', text: 'Failed to parse the CSV file. Please check the format and try again.' }]);
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const clearAttachment = useCallback(() => {
    setAttachedFile(null);
    setParsedLeads(null);
  }, []);

  // Welcome state for new users
  const [welcomeData, setWelcomeData] = useState(null);
  const [brainStats, setBrainStats] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => {
    const flag = localStorage.getItem('onboarding_just_completed');
    if (flag === 'true') return true;
    return false;
  });

  // Remove the flag AFTER the component is stable
  useEffect(() => {
    if (showWelcome) {
      setShowConfetti(true);
      const timer = setTimeout(() => {
        localStorage.removeItem('onboarding_just_completed');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showWelcome]);

  useEffect(() => {
    if (!showWelcome) return;
    const fetchWelcome = async () => {
      try {
        const [statusRes, leadsRes, statsRes] = await Promise.allSettled([
          api.get('/api/onboarding/status').then(r => r.data),
          api.get('/api/leads/?page=1&page_size=1').then(r => r.data),
          api.get('/api/brain/stats').then(r => r.data),
        ]);
        const status = statusRes.status === 'fulfilled' ? statusRes.value : {};
        const leadsRaw = leadsRes.status === 'fulfilled' ? leadsRes.value : [];
        const stats = statsRes.status === 'fulfilled' ? statsRes.value : {};
        const leadCount = Array.isArray(leadsRaw) ? leadsRaw.length : (leadsRaw.total || leadsRaw.leads?.length || 0);
        setWelcomeData({
          companyName: status.company_name || status.company || '',
          modules: status.selected_modules || [],
          leadCount,
          industry: status.industry || '',
          brainSeeded: status.brain_data_seeded || false,
        });
        setBrainStats(stats);
      } catch (err) {
        console.warn('Welcome data fetch:', err);
        setWelcomeData({ companyName: '', modules: [], leadCount: 0, industry: '', brainSeeded: true });
      }
    };
    fetchWelcome();
  }, [showWelcome]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || sending) return;
    setMessages(prev => [...prev, { role: 'user', text: text.trim() }]);
    setInput('');
    setSending(true);

    // Handle CSV lead import if file is attached
    if (parsedLeads?.length > 0) {
      try {
        const res = await api.post('/api/leads/bulk-import', { leads: parsedLeads });
        const { success_count, error_count } = res.data;
        const reply = `Successfully imported **${success_count} leads** from ${attachedFile?.name || 'your CSV file'}!${error_count > 0 ? `\n\n${error_count} row(s) had errors and were skipped.` : ''}\n\nYour new leads are now in the pipeline and ready for outreach.`;
        setMessages(prev => [...prev, {
          role: 'brain',
          text: reply,
          actions: [{ label: 'View Leads', type: 'navigate', path: '/leads', icon: 'leads' }],
        }]);
        clearAttachment();
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Failed to import leads. Please check the file format.';
        setMessages(prev => [...prev, { role: 'brain', text: detail, actions: [] }]);
        clearAttachment();
      } finally {
        setSending(false);
        inputRef.current?.focus();
      }
      return;
    }

    try {
      const res = await api.post('/api/brain/chat', {
        message: text.trim(),
        session_id: sessionId,
      });
      const reply = res.data?.reply || "I'm having trouble right now. Try again in a moment.";
      const actions = res.data?.suggested_actions || [];
      setMessages(prev => [...prev, { role: 'brain', text: reply, actions }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'brain',
        text: "Something went wrong. Check your credit balance or try again.",
        actions: [],
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [sending, sessionId]);

  const executeApiAction = useCallback(async (action, index) => {
    setPendingConfirm(null);
    setLoadingAction(index);
    try {
      const method = (action.method || 'POST').toLowerCase();
      const res = await api[method](action.endpoint);
      const data = res.data || {};
      let summary = `**${action.label}** completed.`;
      if (data.sent !== undefined) summary = `**${action.label}**: ${data.sent} email${data.sent !== 1 ? 's' : ''} sent${data.failed ? `, ${data.failed} failed` : ''} (${data.primary_mode || data.mode || 'done'})`;
      else if (data.count !== undefined) summary = `**${action.label}**: ${data.count} item${data.count !== 1 ? 's' : ''} processed`;
      else if (data.status === 'completed') summary = `**${action.label}** executed successfully.`;
      else if (data.cadence_id) summary = `**Cadence generated** with ${data.total_steps || '?'} steps for **${data.lead_name || 'lead'}**`;
      else if (data.analysis) summary = `**Pipeline analysis refreshed.** Check your dashboard for insights.`;
      setMessages(prev => [...prev, { role: 'brain', text: summary, actions: [] }]);
    } catch (err) {
      const errMsg = err?.response?.data?.detail || 'Action failed. Please try again.';
      setMessages(prev => [...prev, { role: 'brain', text: `Could not execute **${action.label}**: ${errMsg}`, actions: [] }]);
    } finally {
      setLoadingAction(null);
    }
  }, []);

  const handleAction = useCallback(async (action, index) => {
    if (action.action === 'navigate' && action.route) {
      navigate(action.route);
    } else if (action.action === 'api_call' && action.endpoint) {
      setPendingConfirm(index);
    }
  }, [navigate]);

  const cancelConfirm = useCallback(() => setPendingConfirm(null), []);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const hasMessages = messages.length > 0;

  // Notify parent when chat becomes active/inactive
  useEffect(() => {
    onChatActive?.(hasMessages);
  }, [hasMessages, onChatActive]);

  return (
    <div className={`flex flex-col ${hasMessages ? 'h-[calc(100vh-180px)]' : ''}`} data-testid="brain-chat-hero">
      {/* Confetti celebration for new users */}
      <Confetti active={showConfetti} duration={1400} particleCount={5} onComplete={() => setShowConfetti(false)} />

      <style>{`
        @keyframes orbitRing1 { from { transform: rotateX(60deg) rotateZ(0deg); } to { transform: rotateX(60deg) rotateZ(360deg); } }
        @keyframes orbitRing2 { from { transform: rotateX(60deg) rotateY(60deg) rotateZ(0deg); } to { transform: rotateX(60deg) rotateY(60deg) rotateZ(-360deg); } }
        @keyframes brainPulse { 0%, 100% { box-shadow: 0 0 20px rgba(6,182,212,0.2), 0 0 40px rgba(6,182,212,0.1); } 50% { box-shadow: 0 0 30px rgba(6,182,212,0.4), 0 0 60px rgba(6,182,212,0.15); } }
        @keyframes checkStagger { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      {/* Welcome banner for new users who just completed onboarding */}
      {showWelcome && !hasMessages && !hideWelcome && (
        <div className="mb-4 animate-[fadeUp_0.5s_ease-out]" data-testid="onboarding-welcome-banner">
          <div className="relative rounded-2xl overflow-hidden border border-white/20 dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] backdrop-blur-xl">
            <button
              onClick={() => { setShowWelcome(false); setShowConfetti(false); }}
              className="absolute top-3 right-3 p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:text-white/30 dark:hover:text-white/60 transition-colors z-10"
              data-testid="dismiss-welcome-banner"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="px-6 py-5">
              {/* Header with 3D orbital brain */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative w-14 h-14 shrink-0" style={{ perspective: '200px' }}>
                  <div className="absolute inset-2 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg animate-[brainPulse_3s_ease-in-out_infinite] z-10">
                    <Rocket className="h-5 w-5 text-white" />
                  </div>
                  {/* Orbital ring 1 */}
                  <div className="absolute inset-0 rounded-full border border-cyan-400/30 animate-[orbitRing1_6s_linear_infinite]" style={{ transformStyle: 'preserve-3d' }} />
                  {/* Orbital ring 2 */}
                  <div className="absolute inset-0 rounded-full border border-emerald-400/20 animate-[orbitRing2_8s_linear_infinite]" style={{ transformStyle: 'preserve-3d' }} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    {welcomeData?.companyName ? `Welcome, ${welcomeData.companyName}!` : 'Your CRM is Live!'}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-white/40">Your AI Brain is active and working for you</p>
                </div>
              </div>

              {/* Staggered checkmarks */}
              <div className="space-y-2 mb-3">
                {welcomeData ? (
                  <>
                    {welcomeData.brainSeeded && (
                      <div className="flex items-center gap-2 animate-[checkStagger_0.4s_ease-out_0.2s_both]">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span className="text-xs text-slate-600 dark:text-white/60">AI Brain initialized and learning your business</span>
                      </div>
                    )}
                    {welcomeData.leadCount > 0 && (
                      <div className="flex items-center gap-2 animate-[checkStagger_0.4s_ease-out_0.4s_both]">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span className="text-xs text-slate-600 dark:text-white/60">Discovered <strong className="text-slate-900 dark:text-white">{welcomeData.leadCount}</strong> leads matching your ICP</span>
                      </div>
                    )}
                    {welcomeData.modules.length > 0 && (
                      <div className="flex items-center gap-2 animate-[checkStagger_0.4s_ease-out_0.6s_both]">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span className="text-xs text-slate-600 dark:text-white/60">Activated <strong className="text-slate-900 dark:text-white">{welcomeData.modules.length}</strong> modules{welcomeData.industry ? ` for ${welcomeData.industry}` : ''}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 animate-[checkStagger_0.4s_ease-out_0.8s_both]">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <span className="text-xs text-slate-600 dark:text-white/60">Outreach emails drafted and cadences ready</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 animate-[checkStagger_0.4s_ease-out_0.2s_both]">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <span className="text-xs text-slate-600 dark:text-white/60">AI Brain initialized and learning your business</span>
                    </div>
                    <div className="flex items-center gap-2 animate-[checkStagger_0.4s_ease-out_0.4s_both]">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <span className="text-xs text-slate-600 dark:text-white/60">Outreach emails drafted and cadences ready</span>
                    </div>
                  </>
                )}
              </div>

              {/* Brain stats bar */}
              {brainStats && (
                <div className="flex items-center gap-3 py-2 px-3 rounded-lg bg-slate-100/80 dark:bg-white/[0.04] mb-3 animate-[checkStagger_0.4s_ease-out_1s_both]">
                  <Brain className="h-3.5 w-3.5 text-cyan-500 shrink-0" />
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-white/40 flex-wrap">
                    {brainStats.total_decisions > 0 && <span><strong className="text-slate-800 dark:text-white/80">{brainStats.total_decisions}</strong> decisions</span>}
                    {brainStats.scored_leads > 0 && <span><strong className="text-slate-800 dark:text-white/80">{brainStats.scored_leads}</strong> leads scored</span>}
                    {brainStats.pending_email_drafts > 0 && <span><strong className="text-slate-800 dark:text-white/80">{brainStats.pending_email_drafts}</strong> emails drafted</span>}
                  </div>
                </div>
              )}

              <p className="text-[11px] text-cyan-600 dark:text-cyan-400 font-medium flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Try asking: "Show my hot leads" or "What should I do first?"
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero header — only when no messages */}
      {!hasMessages && (
        <div className="text-center py-10 animate-[fadeUp_0.4s_ease-out]">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            {showWelcome && welcomeData?.companyName
              ? `What's first, ${welcomeData.companyName}?`
              : 'What would you like to execute today?'
            }
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Your AI command core is connected to your pipeline, leads, and workflows.
          </p>
        </div>
      )}

      {/* Chat messages */}
      {hasMessages && (
        <div className="flex items-center justify-between px-1 mb-1">
          <span className="text-xs text-muted-foreground font-medium">Brain Chat</span>
          <button
            onClick={() => { setMessages([]); onChatActive?.(false); }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted/50"
            data-testid="brain-new-chat-btn"
          >
            <RotateCcw className="h-3 w-3" />
            New chat
          </button>
        </div>
      )}
      {hasMessages && (
        <div
          ref={scrollRef}
          className="flex-1 space-y-4 mb-3 overflow-y-auto px-1 py-2 scroll-smooth"
          data-testid="brain-chat-hero-messages"
        >
          {messages.map((msg, i) =>
            msg.role === 'user'
              ? <UserMessage key={i} text={msg.text} />
              : <BrainMessage key={i} text={msg.text} actions={msg.actions} onAction={handleAction} loadingAction={i === messages.length - 1 ? loadingAction : null} pendingConfirm={i === messages.length - 1 ? pendingConfirm : null} onConfirm={executeApiAction} onCancel={cancelConfirm} />
          )}
          {sending && <TypingIndicator />}
        </div>
      )}

      {/* Input — always visible, with gradient border animation */}
      <div className="pt-2">
        {/* File attachment preview */}
        {attachedFile && (
          <div className="flex items-center gap-2 mb-2 px-4 py-2.5 rounded-xl bg-blue-50/80 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-800/30 animate-[slideUp_0.2s_ease-out]" data-testid="csv-attachment-preview">
            <FileSpreadsheet className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">{attachedFile.name}</span>
              <span className="text-xs text-blue-600/70 dark:text-blue-400/60 ml-2">{attachedFile.count} leads detected</span>
            </div>
            <button onClick={clearAttachment} className="p-1 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" data-testid="csv-clear-btn">
              <X className="h-3.5 w-3.5 text-blue-500" />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="relative rounded-2xl p-[2px] brain-input-glow">
            <div className="absolute inset-0 rounded-2xl brain-gradient-border" />
            <div className="relative flex items-center gap-1.5 sm:gap-2.5 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-2xl px-3 sm:px-4 py-3.5 shadow-lg shadow-slate-900/5 dark:shadow-black/20">
              <Sparkles className="h-5 w-5 text-cyan-400 dark:text-cyan-500 shrink-0" />
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={input ? '' : (hasMessages ? 'Ask a follow-up...' : (placeholderText + (isTyping && !input && messages.length === 0 ? '|' : '')))}
                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                size={1}
                disabled={sending}
                data-testid="brain-chat-hero-input"
              />
              {/* CSV Attachment button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="csv-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`p-2 rounded-xl transition-all shrink-0 ${
                  attachedFile
                    ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-500 ring-2 ring-blue-300/50 dark:ring-blue-500/30'
                    : 'text-slate-400 dark:text-slate-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20'
                }`}
                title="Attach CSV to import leads"
                data-testid="brain-attach-btn"
              >
                <Paperclip className="h-4 w-4" />
              </button>
              {/* Voice button */}
              <button
                type="button"
                onClick={toggleVoice}
                className={`p-2 rounded-xl transition-all shrink-0 ${
                  isListening
                    ? 'bg-red-50 dark:bg-red-950/30 text-red-500 ring-2 ring-red-300/50 dark:ring-red-500/30 animate-pulse'
                    : 'text-slate-400 dark:text-slate-500 hover:text-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-950/20'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input'}
                data-testid="brain-voice-btn"
              >
                {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
              <Button
                type="submit"
                size="sm"
                disabled={!input.trim() || sending}
                className="h-9 px-2.5 sm:px-4 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white shadow-md shadow-blue-500/20 disabled:opacity-30 text-xs font-semibold shrink-0"
                data-testid="brain-chat-hero-send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : attachedFile ? <><Upload className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Import</span></> : <><Send className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Send</span></>}
              </Button>
            </div>
          </div>
        </form>

        {/* Quick action pills — only when no conversation */}
        {!hasMessages && (
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            {QUICK_ACTIONS.map((action, i) => {
              const Icon = action.icon;
              return (
                <button
                  key={i}
                  onClick={() => sendMessage(action.query)}
                  disabled={sending}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium bg-white/60 dark:bg-white/[0.06] border border-white/20 dark:border-white/[0.08] backdrop-blur-md text-slate-600 dark:text-slate-400 hover:border-cyan-300/50 dark:hover:border-cyan-500/30 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-white/80 dark:hover:bg-white/[0.1] transition-all shadow-sm disabled:opacity-40 hover:shadow-md"
                  data-testid={`brain-quick-action-${i}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gradientSpin {
          0% { --gradient-angle: 0deg; }
          100% { --gradient-angle: 360deg; }
        }
        .brain-gradient-border {
          background: conic-gradient(
            from var(--gradient-angle, 0deg),
            #06b6d4,
            #3b82f6,
            #8b5cf6,
            #06b6d4
          );
          border-radius: 1rem;
          opacity: 0.5;
          animation: gradientSpin 3s linear infinite;
          transition: opacity 0.3s;
        }
        @property --gradient-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        .brain-input-glow:focus-within .brain-gradient-border {
          opacity: 1;
        }
        .brain-input-glow:hover .brain-gradient-border {
          opacity: 0.8;
        }
        .brain-reply ul { list-style: none; padding: 0; margin: 0; }
        .brain-reply ol li { display: list-item; }
        .brain-reply ol li span:first-child { display: none; }
      `}</style>
    </div>
  );
}
